import {
  collection, doc, getDoc, setDoc, query, where, getDocs
} from 'firebase/firestore';
import { db } from '../firebase';
import type { ApiResponse, Booking, BookingRequest, Property } from '../types';

// ---------- helpers ----------

// Overlap test: existing [checkin, checkout) vs requested [start, end)
const overlaps = (aIn: string, aOut: string, bIn: string, bOut: string) =>
  aIn < bOut && bIn < aOut;

// ---------- public API ----------

/**
 * Fetch a property's details (prices, max_guests, min_nights, etc.) from Firestore.
 */
export const fetchProperty = async (propertyId: string): Promise<Property | null> => {
  try {
    const snap = await getDoc(doc(db, 'properties', propertyId));
    if (!snap.exists()) return null;
    return snap.data() as Property;
  } catch (error) {
    console.error('Error fetching property:', error);
    return null;
  }
};

/**
 * Fetch all blocked date ranges for a property
 * (so a calendar can grey out taken dates)
 */
export const fetchAvailableDates = async (
  propertyId: string
): Promise<AvailableDate[]> => {
  const q = query(
    collection(db, 'blockedRanges'),
    where('property_id', '==', propertyId)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const r = d.data();
    return {
      date: r.start,
      start: r.start,
      end: r.end,
      isAvailable: false,
    };
  });
};

/**
 * Check if a specific date range is free for a property
 */
export const checkAvailability = async (
  propertyId: string,
  checkin: string,
  checkout: string
): Promise<boolean> => {
  const q = query(
    collection(db, 'blockedRanges'),
    where('property_id', '==', propertyId)
  );
  const snap = await getDocs(q);
  const clash = snap.docs.some((d) => {
    const r = d.data();
    return overlaps(r.start, r.end, checkin, checkout);
  });
  return !clash;
};

/**
 * Save a new booking request with status = PENDING
 */
export const submitBookingRequest = async (
  bookingRequest: BookingRequest
): Promise<ApiResponse<{ bookingId: string; checkoutUrl: string }>> => {
  try {
    const bookingRef = doc(collection(db, 'bookings'));
    const booking: Booking = {
      id: bookingRef.id,
      property_id: bookingRequest.property_id,
      guest_name: bookingRequest.guest_name,
      guest_email: bookingRequest.guest_email,
      checkin: bookingRequest.checkin,
      checkout: bookingRequest.checkout,
      nights: bookingRequest.nights ?? 0,
      total_price: bookingRequest.total_price ?? 0,
      source: 'DIRECT',
      status: 'PENDING',
      created_at: new Date().toISOString(),
      adults: bookingRequest.adults ?? 1,
      children: bookingRequest.children ?? 0,
    };
    // Only add optional fields if they have a value (Firestore rejects undefined)
    if (bookingRequest.guest_phone) booking.guest_phone = bookingRequest.guest_phone;
    if (bookingRequest.special_requests) {
      (booking as Booking).special_requests = bookingRequest.special_requests;
    }
    await setDoc(bookingRef, booking);

    return {
      success: true,
      data: { bookingId: bookingRef.id, checkoutUrl: '' },
    };
  } catch (error) {
    console.error('Error submitting booking request:', error);
    return { success: false, error: 'Failed to submit booking request' };
  }
};

/**
 * STRIPE DISABLED FOR NOW:
 * Saves the booking as PENDING. Admin handles payment manually.
 */
export const createCheckoutSession = async (
  propertyId: string,
  checkin: string,
  checkout: string,
  adults: number,
  children: number,
  guestEmail: string,
  guestName: string
): Promise<ApiResponse<{ sessionId: string; url: string }>> => {
  try {
    const nights = Math.round(
      (new Date(checkout).getTime() - new Date(checkin).getTime()) / 86400000
    );

    const priceRes = await submitBookingRequest({
      property_id: propertyId,
      checkin,
      checkout,
      adults,
      children,
      guest_name: guestName,
      guest_email: guestEmail,
      total_price: 0,
      nights,
    } as BookingRequest);

    if (!priceRes.success || !priceRes.data) {
      return { success: false, error: 'Could not save your booking. Please try again.' };
    }

    // url is empty → UI shows the "payment coming soon" confirmation
    return { success: true, data: { sessionId: priceRes.data.bookingId, url: '' } };
  } catch (error) {
    console.error('Error creating booking:', error);
    return { success: false, error: 'Something went wrong. Please try again.' };
  }
};

// Re-export for other files that may need it later
// export { Timestamp };